import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { useAppStore } from '@/store/app-store'
import { useLayoutStore } from '@/store/layout-store'
import { useLoggerStore } from '@/store/logger'
import { useNotificationsQueue } from '@/store/notification-que/notifications-queue'

const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000
const AUTO_SHOW_DELAY_MS = 20000

export const useModalInstallGooglePlayStore = defineStore(
    'modal_install_google_play',
    () => {
        const lastDismissedAt = ref<number | null>(null)
        const autoShowTimeoutId = ref<ReturnType<typeof setTimeout> | null>(null)
        // true only while removing the banner because of fullscreen — a session-only
        // hide that must skip the 2-week dismissal and the 'closed' analytics event
        let isFullscreenClose = false
        let stopFullscreenWatch: (() => void) | null = null

        const canShowModal = computed(() => {
            if (lastDismissedAt.value === null) {
                return true
            }
            const now = Date.now()
            return now - lastDismissedAt.value >= TWO_WEEKS_MS
        })

        function markDismissed() {
            lastDismissedAt.value = Date.now()
        }

        function reset() {
            lastDismissedAt.value = null
        }

        function init() {
            const appStore = useAppStore()
            const layoutStore = useLayoutStore()
            const loggerStore = useLoggerStore()
            const queue = useNotificationsQueue()

            if (appStore.platform === 'android' && appStore.isPortalEnv && !appStore.isAppEnv && canShowModal.value) {
                autoShowTimeoutId.value = setTimeout(() => {
                    if (layoutStore.fullScreen) {
                        return
                    }
                    queue.request({
                        name: 'install-google-play-modal',
                        weight: 1,
                        onOpen: () => {
                            loggerStore.logEvent({
                                event: 'custom_event',
                                eventName: 'google_play_modal_opened',
                            })
                            layoutStore.openInstallGooglePlayModal()
                        },
                        onClose: () => {
                            layoutStore.closeInstallGooglePlayModal()
                            if (!isFullscreenClose) {
                                markDismissed()
                                loggerStore.logEvent({
                                    event: 'custom_event',
                                    eventName: 'google_play_modal_closed',
                                })
                            }
                        },
                    })
                }, AUTO_SHOW_DELAY_MS)

                // Entering fullscreen removes the banner from the queue (so it does not
                // stall other notifications) without dismissing it, and it is not
                // brought back on exit.
                stopFullscreenWatch = watch(
                    () => layoutStore.fullScreen,
                    (isFullScreen) => {
                        if (isFullScreen) {
                            isFullscreenClose = true
                            queue.remove('install-google-play-modal')
                            isFullscreenClose = false
                        }
                    },
                )
            }
        }

        function dispose() {
            const queue = useNotificationsQueue()

            if (autoShowTimeoutId.value) {
                clearTimeout(autoShowTimeoutId.value)
                autoShowTimeoutId.value = null
            }
            stopFullscreenWatch?.()
            stopFullscreenWatch = null
            queue.remove('install-google-play-modal')
        }

        return {
            lastDismissedAt,
            canShowModal,
            reset,
            init,
            dispose,
        }
    },
    {
        persist: {
            key: 'modal_install_google_play',
            pick: ['lastDismissedAt'],
        },
    },
)
